import axios, { AxiosInstance } from 'axios';
import {
  ProductAttributesConfig,
  ProductData,
  ProductDataAttributes,
  AvailableOption,
  AvailableGeometryOption,
} from './types';

/**
 * Generic service for fetching and transforming product attributes
 * from the Vivid API. Framework- and project-agnostic.
 *
 * ## Basic usage
 * ```ts
 * import { ProductAttributesService } from './productAttributes';
 *
 * const service = new ProductAttributesService({
 *   apiBaseUrl: 'https://renderadmin.vivid3d.tech',
 *   productId: 2418,
 * });
 *
 * const attributes = await service.getAttributes();
 * // attributes: Record<string, ProductDataAttributes>
 * ```
 *
 * ## Multi-product preloading
 * ```ts
 * await service.preloadProducts([2644, 2645]);
 * const a = await service.getAttributes(2644); // from cache
 * const b = await service.getAttributes(2645); // from cache
 * ```
 *
 * ## Usage with Redux / Zustand / any store
 *
 * ### 1. Define slice state and reducer
 * ```ts
 * import { createSlice, PayloadAction } from '@reduxjs/toolkit';
 * import { ProductDataAttributes } from './productAttributes';
 *
 * interface ProductState {
 *   dataProduct: Record<string, ProductDataAttributes>;
 * }
 *
 * const initialState: ProductState = { dataProduct: {} };
 *
 * export const productSlice = createSlice({
 *   name: 'product',
 *   initialState,
 *   reducers: {
 *     setDataProduct(
 *       state,
 *       action: PayloadAction<{ dataProduct: Record<string, ProductDataAttributes> }>
 *     ) {
 *       state.dataProduct = action.payload.dataProduct;
 *     },
 *   },
 * });
 * ```
 *
 * ### 2. Load attributes into store
 * ```ts
 * const service = new ProductAttributesService({
 *   apiBaseUrl: 'https://renderadmin.vivid3d.tech',
 *   productId: 1234,
 * });
 *
 * export async function loadProductAttributes(store: Store) {
 *   const dataProduct = await service.getAttributes();
 *   store.dispatch(setDataProduct({ dataProduct }));
 * }
 * ```
 *
 * ## Copying to another project
 * 1. Copy `productAttributes/` folder (ProductAttributesService.ts + types.ts + index.ts)
 * 2. Install `axios` (`npm i axios`)
 * 3. Set your own `apiBaseUrl` and `productId`
 * 4. Wire into your store as shown above
 */
export class ProductAttributesService {
  private axiosInstance: AxiosInstance;
  private productId: number;
  private cache = new Map<number, Record<string, ProductDataAttributes>>();

  constructor(private config: ProductAttributesConfig) {
    this.productId = config.productId;
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
    });
  }

  /** Update the default product ID */
  public setProductId(id: number): void {
    this.productId = id;
  }

  /** Get the default product ID */
  public getProductId(): number {
    return this.productId;
  }

  /**
   * Fetch and transform product attributes.
   * Results are cached — subsequent calls for the same product ID return
   * the cached value unless `useCache` is set to `false`.
   */
  public async getAttributes(
    productId?: number,
    useCache = true
  ): Promise<Record<string, ProductDataAttributes>> {
    const id = productId ?? this.productId;

    if (useCache && this.cache.has(id)) {
      return this.cache.get(id)!;
    }

    const productData = await this.fetchProductData(id);

    if (!productData) {
      return {};
    }

    const optionGroups = (productData.availableOptions || []).map(
      (g) => ({ group: g, type: 'option' as const })
    );
    const geometryGroups = (productData.availableGeometryOptions || []).map(
      (g) => ({ group: g, type: 'geometry' as const })
    );

    const result = [...optionGroups, ...geometryGroups].reduce<
      Record<string, ProductDataAttributes>
    >((acc, { group, type }) => {
      const transformed = this.transformOptions(group, type);
      return { ...acc, ...transformed };
    }, {});

    this.cache.set(id, result);
    return result;
  }

  /**
   * Preload multiple products in parallel. All results are cached.
   */
  public async preloadProducts(productIds: number[]): Promise<void> {
    await Promise.all(productIds.map((id) => this.getAttributes(id)));
  }

  /**
   * Remove cached data for a product (or all products).
   */
  public invalidateCache(productId?: number): void {
    if (productId !== undefined) {
      this.cache.delete(productId);
    } else {
      this.cache.clear();
    }
  }

  /** Check if a product is already cached */
  public isCached(productId?: number): boolean {
    return this.cache.has(productId ?? this.productId);
  }

  /**
   * Fetch raw product data from the API (unprocessed, uncached).
   */
  public async getRawProductData(
    productId?: number
  ): Promise<ProductData | null> {
    return this.fetchProductData(productId ?? this.productId);
  }

  // ── Private ──

  private async fetchProductData(
    productId: number
  ): Promise<ProductData | null> {
    try {
      const response = await this.axiosInstance.get(
        `/products/${productId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `[ProductAttributesService] Error fetching product ${productId}:`,
        error
      );
      return null;
    }
  }

  private transformOptions(
    data: AvailableOption | AvailableGeometryOption,
    type: 'option' | 'geometry'
  ): Record<string, ProductDataAttributes> {
    const result: Record<string, ProductDataAttributes> = {};

    data.options.forEach((item) => {
      result[item.name] = {
        id: item.id,
        name: data.proxyName,
        type,
        values: item.variants.map((variant) => ({
          id: variant.id,
          name: variant.name,
          enabled: variant.enabled,
          image: variant.image ?? null,
          description: ('description' in variant ? variant.description : null) ?? null,
        })),
      };
    });

    return result;
  }
}
